import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { PieChart as PieChartIcon } from 'lucide-react';
import { SourceMetricRow } from '../../utils/leadAnalytics';

export interface SourceSlice {
  source: string;
  count: number;
  color: string;
  percentage: number;
}

interface Props {
  slices: SourceSlice[];
  metrics?: SourceMetricRow[];
  title: string;
  subtitle?: string;
}

const SourceBreakdownPiePanel: React.FC<Props> = ({ slices, metrics, title, subtitle }) => {
  const metricMap = useMemo(() => {
    const map = new Map<string, SourceMetricRow>();
    metrics?.forEach(m => map.set(m.source, m));
    return map;
  }, [metrics]);

  const total = slices.reduce((s, x) => s + x.count, 0);

  return (
    <div className="sa-hub-pie-trigger-wrap">
      <button
        type="button"
        className="sa-hub-pie-trigger"
        aria-label="View lead source breakdown"
      >
        <PieChartIcon size={16} strokeWidth={2.25} />
      </button>
      <div className="sa-hub-pie-popover" role="tooltip">
        <div className="sa-hub-pie-popover-head">
          <strong>{title}</strong>
          {subtitle && <span>{subtitle}</span>}
        </div>
        {total === 0 ? (
          <p className="sa-hub-muted">No leads in this view.</p>
        ) : (
          <div className="sa-hub-pie-popover-body">
            <div className="sa-hub-pie-chart-wrap">
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={68}
                    paddingAngle={2}
                  >
                    {slices.map(s => (
                      <Cell key={s.source} fill={s.color} stroke="var(--color-surface)" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, _name, item) => {
                      const pct = total > 0 ? ((Number(value) / total) * 100).toFixed(0) : '0';
                      return [`${value} (${pct}%)`, item.payload.source];
                    }}
                    contentStyle={{
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      borderRadius: 8,
                      border: '1px solid var(--color-border)',
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="sa-hub-pie-center">
                <span className="sa-hub-pie-center-value">{total}</span>
                <span className="sa-hub-pie-center-label">leads</span>
              </div>
            </div>
            <ul className="sa-hub-pie-list">
              {slices.map(s => {
                const m = metricMap.get(s.source);
                return (
                  <li key={s.source} className="sa-hub-pie-list-row">
                    <span className="sa-source-dot" style={{ background: s.color }} />
                    <span className="sa-hub-pie-list-name">{s.source}</span>
                    <span className="sa-hub-pie-list-stat">{s.count}</span>
                    <span className="sa-hub-pie-list-pct">{s.percentage.toFixed(0)}%</span>
                    {m && m.dealCount > 0 && (
                      <span className="sa-hub-pie-list-meta">
                        {m.conversionRate.toFixed(0)}% conv · {m.winRate.toFixed(0)}% win
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceBreakdownPiePanel;
